/**
 * "Tell me when a party like this opens."
 *
 * The honest answer to an empty list at eight on a Tuesday is not "try
 * again later" - it is to take the search off the person and give it to the
 * server. One alert, fired once, when a party that matches is posted.
 *
 * Deliberately one-shot. A standing alarm that pings every Valorant party for
 * the rest of the month is how notification settings get turned off entirely;
 * this fires, tells you, and is gone.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PartyWatch } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Button, useToast } from './ui';

export function useWatches(enabled = true) {
  const me = useSession((s) => s.me);
  return useQuery({
    queryKey: ['watches'],
    queryFn: () => api.get<{ watches: PartyWatch[]; max: number }>('/api/watches'),
    enabled: enabled && !!me,
    staleTime: 60_000
  });
}

/** The button under an empty list, and the alerts already waiting. */
export function PartyAlerts({ gameId, region, mode }: { gameId?: string; region?: string; mode?: string }) {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data } = useWatches();
  const [justAdded, setJustAdded] = useState(false);

  const watches = data?.watches ?? [];
  const already = watches.some((w) => (w.gameId ?? '') === (gameId ?? '') && (w.region ?? '') === (region ?? ''));

  const add = useMutation({
    mutationFn: () => api.post<{ watch: PartyWatch }>('/api/watches', { gameId: gameId || null, region: region || null, mode: mode || null }),
    onSuccess: () => {
      setJustAdded(true);
      void queryClient.invalidateQueries({ queryKey: ['watches'] });
      toast('We will tell you the moment one opens', 'good');
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const drop = useMutation({
    mutationFn: (id: string) => api.del('/api/watches/' + id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['watches'] }),
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const label = (w: { gameId: string | null; region: string | null }) => {
    const g = w.gameId ? catalog?.games.find((x) => x.id === w.gameId)?.name : null;
    const r = w.region ? catalog?.regions.find((x) => x.id === w.region)?.name : null;
    return [g ?? 'Any game', r].filter(Boolean).join(' · ');
  };

  if (!me) return null;
  if (!gameId && !region && !watches.length) return null;

  return (
    <div className="flex flex-col items-center gap-2">
      {gameId || region ? (
        already || justAdded ? (
          <span className="flex items-center gap-2 rounded-full bg-accent-soft px-3.5 py-1.5 text-[13px] font-semibold text-accent">
            <Icon name="bell" size={14} />
            You will be told when one opens
          </span>
        ) : (
          <Button
            kind="outline"
            icon={<Icon name="bell" size={16} />}
            disabled={add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Setting it up…' : 'Tell me when one opens'}
          </Button>
        )
      ) : null}

      {watches.length ? (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          <span className="text-[12px] text-mute">Waiting on:</span>
          {watches.map((w) => (
            <span key={w.id} className="flex items-center rounded-full bg-surface-2 py-1 pr-1 pl-3 text-[12px] font-semibold text-dim">
              {label(w)}
              <button
                type="button"
                aria-label={'Stop waiting for ' + label(w)}
                onClick={() => drop.mutate(w.id)}
                className={cn(
                  'ml-1.5 grid h-5 w-5 place-items-center rounded-full text-ghost outline-none transition-colors',
                  'hover:bg-surface-3 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]'
                )}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.6" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
